import React, { useState } from "react";
import { useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
} from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { CustomTheme } from "../../../../types/customTheme";
import CardCommon from "../../../../components/card/CardCommon";
import { Skeleton } from "@material-ui/lab";
import { useTheme } from "@material-ui/core/styles";
import ChangeRequestDetailsModal from "./ChangeRequestDetailsModal";


export interface ChangeRequest {
  changeRequestId: string;
  locationId: string;
  locationName: string;
  type: string;
  requestedBy: string;
  requestedOn: string;
  status: string;
  [key: string]: any;
}

interface Props {
  requests: ChangeRequest[];
  loading: boolean;
  error: string;
  onReload: () => void;
}

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    tableContainer: {
      backgroundColor: theme.palette.background.default,
      boxShadow: "none",
    },
    tableRow: {
      "& td": {
        borderBottom: "none",
        padding: "2px 4px",
        fontSize: "12px",
        color: theme.palette.text.primary,
        border: "none",
      },
    },
    cardWrapperCell: {
      padding: 4,
      borderBottom: "none",
      border: "none",
    },
    table: {
      backgroundColor: "transparent",
      borderCollapse: "separate",
      borderSpacing: 0,
      border: "none",
    },
    statusPin: {
      borderRadius: 8,
      padding: "2px 8px",
      fontSize: "12px",
      textAlign: "center",
      fontWeight: 600,
      minWidth: 70,
      display: "inline-block",
      width: "fit-content",
    },
    noData: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(2),
      fontSize: "16px",
      textAlign: "center",
    },
  }),
);



const ChangeRequests: React.FC<Props> = ({
  requests,
  loading,
  error,
  onReload,
}) => {
  const classes = useStyles();
  const theme = useTheme<CustomTheme>();
  const [selectedRequest, setSelectedRequest] = useState<ChangeRequest | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);


  const handleRowClick = (request: ChangeRequest) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
  };

  const getStatusStyles = (status: string) => {
    const map: Record<string, { backgroundColor: string; color: string }> = {
      approved: {
        backgroundColor: theme.palette.custom.green.main || "#4CAF50",
        color: theme.palette.custom.green.contrastText || "#fff",
      },
      trial: {
        backgroundColor: theme.palette.custom.blue.main || "#2196f3",
        color: theme.palette.custom.blue.contrastText || "#fff",
      },
      denied: {
        backgroundColor: theme.palette.custom.red.main || "#f44336",
        color: theme.palette.custom.red.contrastText || "#fff",
      },
      changing: {
        backgroundColor: theme.palette.custom.yellow.main || "#FFEB3B",
        color: theme.palette.custom.yellow.contrastText || "#000",
      },
      requested: {
        backgroundColor: theme.palette.custom.yellow.main || "#FFEB3B",
        color: theme.palette.custom.yellow.contrastText || "#000",
      },
    };
    return (
      map[status.toLowerCase()] || { backgroundColor: "#ccc", color: "#000" }
    );
  };

  return (
    <>
      <Box px={3} pt={3} pb={3}>
        <Typography variant="h4" align="left">
          Change Requests
        </Typography>
      </Box>

      <CardCommon
        backgroundColor="entity_background"
        style={{ marginTop: 16, border: "none", boxShadow: "none" }}
      >
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell colSpan={6} className={classes.cardWrapperCell}>
                  <CardCommon
                    backgroundColor="table_header_background"
                    style={{ borderRadius: 10, marginBottom: 0 }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      px={1.5}
                      height="42px"
                      alignItems="center"
                    >
                      <Box width="20%">
                        <strong>Location</strong>
                      </Box>
                      <Box width="20%">
                        <strong>Type</strong>
                      </Box>
                      <Box width="20%">
                        <strong>Requested By</strong>
                      </Box>
                      <Box width="20%">
                        <strong>Requested On</strong>
                      </Box>
                      <Box width="10%">
                        <strong>Status</strong>
                      </Box>
                    </Box>
                  </CardCommon>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                [...Array(6)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={6} className={classes.cardWrapperCell}>
                      <Skeleton
                        variant="rect"
                        height={36}
                        style={{ borderRadius: 10 }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography className={classes.noData}>{error}</Typography>
                  </TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography className={classes.noData}>
                      No change requests found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((req) => (
                  <TableRow
                    key={req.changeRequestId}
                    className={classes.tableRow}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(req)}
                  >
                    <TableCell colSpan={6} className={classes.cardWrapperCell}>
                      <CardCommon
                        backgroundColor="entity_background"
                        style={{ borderRadius: 10, marginBottom: 1 }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          flexWrap="wrap"
                          px={1.5}
                          height="42px"
                          alignItems="center"
                        >
                          <Box width="20%">
                              {req.locationName || req.locationId}
                          </Box>
                          <Box width="20%">{req.type}</Box>
                          <Box width="20%">{req.requestedBy}</Box>
                          <Box width="20%">
  {new Date(req.requestedOn).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })}{" "}
  -{" "}
  {new Date(req.requestedOn).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })}
</Box>

                          <Box width="10%">
                            <span
                              className={classes.statusPin}
                              style={getStatusStyles(req.status)}
                            >
                              {req.status.charAt(0).toUpperCase() +
                                req.status.slice(1)}
                            </span>
                          </Box>
                        </Box>
                      </CardCommon>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardCommon>

      <ChangeRequestDetailsModal
        open={isModalOpen}
        onClose={handleModalClose}
        request={selectedRequest}
        onSuccess={onReload}
      />
    </>
  );
};

export default ChangeRequests;
